import { LatitudeLongitude } from '@aloft/toolkit-shared';
import { Flex, Modal, Typography } from '@aloft/toolkit-sui';

import { useCallback, useEffect, useRef, useState } from 'react';

import MapBox from '../../../components/map/MapBox.ts';
import type { Coordinates } from '../../../components/map/types';
import { useMap } from '../../../components/map/useMap.ts';
import { useUpdateSubjectPin } from '../../../components/map/useUpdateSubjectPin.ts';

export const SubjectPropertyLocationModal = ({
  onClose,
  isOpen,
  currentCoordinates,
}: {
  onClose: (newCoordinates?: LatitudeLongitude) => void;
  isOpen: boolean;
  currentCoordinates?: LatitudeLongitude;
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);

  // Local state for tracking coordinates during editing
  // This is separate from the prop to allow preview without saving
  const [localCoordinates, setLocalCoordinates] = useState<Coordinates | undefined>(
    currentCoordinates
      ? { lat: currentCoordinates.latitude, lng: currentCoordinates.longitude }
      : undefined,
  );

  // Reset local coordinates when modal opens with new props
  useEffect(() => {
    if (isOpen && currentCoordinates) {
      setLocalCoordinates({
        lat: currentCoordinates.latitude,
        lng: currentCoordinates.longitude,
      });
    }
  }, [isOpen, currentCoordinates]);

  // Cleanup map when modal closes
  useEffect(() => {
    if (!isOpen && mapRef.current) {
      mapRef.current.destroy();
      mapRef.current = null;
    }
  }, [isOpen]);

  // Handle map clicks to update coordinates
  const handleMapClick = useCallback((coordinates: Coordinates) => {
    setLocalCoordinates(coordinates);
  }, []);

  // Setup map interaction handlers when map loads
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Click handler
    const onMapClickHandler = (e: any) => {
      // Check if clicking on an existing pin
      const existingLayers = ['pins', 'subject'].filter((layerId) => map.getLayer(layerId));
      if (existingLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: existingLayers });
        if (features.length > 0) return;
      }

      handleMapClick({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    };

    // Cursor feedback
    const onMouseMove = (e: any) => {
      const existingLayers = ['pins', 'subject'].filter((layerId) => map.getLayer(layerId));
      let isOverFeature = false;
      if (existingLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: existingLayers });
        isOverFeature = features.length > 0;
      }
      map.getCanvas().style.cursor = isOverFeature ? 'pointer' : 'crosshair';
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    // Attach handlers
    map.on('click', onMapClickHandler);
    map.on('mousemove', onMouseMove);
    map.on('mouseleave', onMouseLeave);
  }, [handleMapClick]);

  // Initialize map
  useMap({
    mapContainerRef,
    mapRef,
    center: localCoordinates ?? { lat: 39.8283, lng: -98.5795 }, // Center of USA as fallback
    isEnabled: isOpen,
    zoom: 15,
    callbacks: {
      onMapLoad: handleMapLoad,
    },
    // Note: We don't pass subject here because we'll manage the pin ourselves
  });

  // Update subject pin when local coordinates change
  useUpdateSubjectPin({
    mapRef,
    coordinates: localCoordinates,
  });

  // Confirm button - save the coordinates
  const handleConfirm = () => {
    if (!localCoordinates) {
      onClose(); // No coordinates to save
      return;
    }

    onClose({
      latitude: localCoordinates.lat,
      longitude: localCoordinates.lng,
    });
  };

  // Cancel button - discard changes
  const handleCancel = () => {
    onClose(); // Pass no arguments = don't update coordinates
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Verify Subject Location"
      showCloseButton={false}
      closeOnEsc={false}
      primaryAction={{ text: 'Confirm', onClick: handleConfirm }}
      secondaryAction={{ text: 'Cancel', onClick: handleCancel }}
    >
      <Typography variant="body1">
        Click anywhere on the map to relocate the subject pin to the correct location.
      </Typography>
      <Flex
        ref={mapContainerRef}
        height="400px"
        width="100%"
        position="relative"
        marginTop="16px"
        borderRadius="4px"
      />
    </Modal>
  );
};
